/**
 * ListDetailsPanel — shared detail panel substrate for templates and checklists.
 *
 * Post-A4 this component does not know whether it's rendering a template or a
 * checklist; the entity-specific bits (action set, metadata layout, status
 * badge, progress, feedback wrapping) are passed in by the thin shell that
 * wraps it (`ChecklistDetailsPanel` / `TemplateDetailsPanel`).
 */

import { EntityForm } from '@/components/forms/EntityForm';
import {
  Panel,
  type PanelAction,
  type RoutedPanelProps,
} from '@/components/Layout/Panel/Panel';
import { Badge } from '@/components/ui/Badge/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useStackNavigate } from '@/app/navigation/stack-links';
import { useStackPath } from '@/app/navigation/useStackPath';
import { useEntityForm } from '@/hooks/useEntityForm';
import { getDirtyValues } from '@/lib/form-utils';
import { adaptActionsForPanel } from '@/utils/adaptActionsForPanel';
import type { Layout } from '@/components/forms/fields/types';
import type { RowAction } from '@/components/ui/data-table/types';
import {
  calculateProgress,
  createListEvent,
  type List,
  type ListEvent,
  type ListSummary,
} from '@certhon/models';
import { Check, FileDown, Languages, Pencil, X } from 'lucide-react';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import {
  useChecklist,
  usePostListEventsMutation,
} from '@/features/lists/lists.hooks';
import { ListProvider, useListContext } from '@/features/lists/context';
import type { PreviewLocale } from '@/features/lists/components/PreviewLocaleContext';
import { ListSummaryFooter } from './ListSummaryFooter';
import { ListTreeEditor } from './ListTreeEditor';

// =============================================================================
// Public API — the inner panel content (assumes a ListProvider in scope)
// =============================================================================

export interface ListDetailsPanelProps extends RoutedPanelProps {
  /** Metadata form layout for this entity type. */
  layout: Layout;
  /**
   * Build the entity-specific row actions for the user. The substrate adapts
   * these into PanelActions and disables them while editing.
   */
  getActions: (userId: number | null) => RowAction<ListSummary>[];
  /** Whether to show a status badge (Open/Completed) in the header. */
  showStatusBadge?: boolean;
  /** Whether to show a "Download PDF" panel action. */
  showPdfDownload?: boolean;
  /**
   * When set, builds a `progress` object from the BuiltList tree and passes
   * it to the summary footer. Templates pass `false`, checklists pass `true`.
   */
  showProgress?: boolean;
  /**
   * Optional wrapper around the panel body — used by templates to install
   * a `FieldFeedbackProvider`. Receives the body and returns wrapped JSX.
   */
  wrapBody?: (body: ReactNode, list: List) => ReactNode;
}

// =============================================================================
// Panel entry point — wraps in ListProvider
// =============================================================================

export const ListDetailsPanel = ({
  params,
  path,
  basePath,
  layout,
  getActions,
  showStatusBadge,
  showPdfDownload,
  showProgress,
  wrapBody,
  ...panelProps
}: ListDetailsPanelProps) => {
  const listId = params.templateId || params.checklistId;
  const nodeId = params.nodeId || params.questionId;

  if (!listId) {
    return (
      <Panel {...panelProps} title="List">
        <p className="text-muted-foreground text-sm">No list ID provided.</p>
      </Panel>
    );
  }

  return (
    <ListProvider
      listId={listId}
      key={listId}
      // Show the panel straight away while the list loads, instead of
      // leaving the previous one up or an empty column.
      fallback={
        <Panel {...panelProps}>
          <ListDetailsSkeleton />
        </Panel>
      }
    >
      <ListDetailsPanelContent
        listId={listId}
        nodeId={nodeId}
        path={path}
        basePath={basePath}
        layout={layout}
        getActions={getActions}
        showStatusBadge={showStatusBadge}
        showPdfDownload={showPdfDownload}
        showProgress={showProgress}
        wrapBody={wrapBody}
        {...panelProps}
      />
    </ListProvider>
  );
};

function ListDetailsSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true">
      <div className="space-y-3">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
      <Skeleton className="h-10 w-full" />
      <div className="space-y-2">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Inner content — has access to ListContext
// =============================================================================

interface ContentProps extends Omit<RoutedPanelProps, 'params'> {
  listId: string;
  nodeId?: string;
  layout: Layout;
  getActions: (userId: number | null) => RowAction<ListSummary>[];
  showStatusBadge?: boolean;
  showPdfDownload?: boolean;
  showProgress?: boolean;
  wrapBody?: (body: ReactNode, list: List) => ReactNode;
}

function ListDetailsPanelContent({
  listId,
  nodeId,
  basePath,
  layout,
  getActions,
  showStatusBadge = false,
  showPdfDownload = false,
  showProgress = false,
  wrapBody,
  ...panelProps
}: ContentProps) {
  const stackPath = useStackPath();
  const {
    list,
    builtList,
    hasChanges,
    pendingEvents,
    revert,
    mode,
    setMode,
    user,
  } = useListContext();
  const navigate = useStackNavigate();
  const [previewLocale, setPreviewLocale] = useState<PreviewLocale>('nl');

  const listQuery = useChecklist(listId, { suspense: false });
  // Layout is supplied by the caller and is wider than LayoutToFormValues can
  // narrow to here (substrate doesn't know which entity-specific shape it is),
  // so we pass `data` through as-is — the runtime values match the layout.
  const entityForm = useEntityForm({
    layout,
    ...listQuery,
    data: listQuery.data as Record<string, unknown> | undefined,
  });
  const { form, setFormMode } = entityForm;
  const updateListMutation = usePostListEventsMutation();

  // -------------------------------------------------------------------------
  // Mode management
  // -------------------------------------------------------------------------

  const enterEditMode = useCallback(() => {
    setMode('edit');
    setFormMode('edit');
  }, [setMode, setFormMode]);

  const handleRevert = useCallback(() => {
    form.reset();
    setFormMode('view');
    revert();
    // Clear the override so each entity returns to its natural default
    // (fill-in for open checklists, view for templates / closed lists).
    // Hard-coding 'view' here leaves checklists stuck with disabled controls.
    setMode(null);
  }, [form, setFormMode, revert, setMode]);

  const handleSave = useCallback(async () => {
    try {
      // Collect all events into a single atomic batch:
      // metadata changes (MODIFY_LIST) + pending tree structure events
      const allEvents: ListEvent[] = [];

      const dirtyValues = getDirtyValues(
        form.formState.dirtyFields,
        form.getValues(),
      );
      if (Object.keys(dirtyValues).length > 0) {
        allEvents.push(
          createListEvent('MODIFY_LIST', {
            user_id: user.recnum,
            list_id: listId,
            payload: dirtyValues,
          }),
        );
      }

      // Append pending tree events from context
      allEvents.push(...pendingEvents);

      if (allEvents.length > 0) {
        await updateListMutation.mutateAsync(allEvents);
      }

      // Clear pending events (already posted)
      revert();

      form.reset(form.getValues());
      setFormMode('view');
      setMode(null);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : error?.toString());
    }
  }, [
    form,
    setFormMode,
    user.recnum,
    listId,
    pendingEvents,
    updateListMutation,
    revert,
    setMode,
  ]);

  const isEditing = mode === 'edit';

  // -------------------------------------------------------------------------
  // Actions menu
  // -------------------------------------------------------------------------

  const toggleLocale = useCallback(() => {
    setPreviewLocale((prev) => (prev === 'nl' ? 'en' : 'nl'));
  }, []);

  const handleDownloadPdf = useCallback(() => {
    const link = document.createElement('a');
    link.href = `/api/lists/${listId}/pdf`;
    link.download = `list-${listId.slice(0, 8)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [listId]);

  const actions = useMemo(() => {
    if (!list) return [];

    const panelActions: PanelAction[] = [];

    // Language toggle — always available, including edit mode
    panelActions.push({
      icon: Languages,
      label: `Preview: ${previewLocale === 'nl' ? 'Nederlands' : 'English'}`,
      onCalled: toggleLocale,
    });

    // Entity-specific actions (disabled while editing) — supplied by caller
    const rowActions = getActions(user?.recnum ?? null);
    for (const a of adaptActionsForPanel(rowActions, list)) {
      panelActions.push(isEditing ? { ...a, disabled: true } : a);
    }

    if (showPdfDownload) {
      panelActions.push({
        icon: FileDown,
        label: 'Download PDF',
        onCalled: handleDownloadPdf,
        disabled: isEditing,
      });
    }

    return panelActions;
  }, [
    list,
    getActions,
    showPdfDownload,
    isEditing,
    user,
    previewLocale,
    toggleLocale,
    handleDownloadPdf,
  ]);

  // -------------------------------------------------------------------------
  // Navigation
  // -------------------------------------------------------------------------

  // Selection: "I'm looking at this node"
  const handleSelectField = useCallback(
    (fieldId: string) => {
      if (isEditing) {
        // Edit mode: preserve depth — keep details open if already open
        const hasChildPanel =
          stackPath !== basePath && stackPath.includes('/details');
        const suffix = hasChildPanel ? '/details' : '';
        navigate({ to: `${basePath}/${fieldId}${suffix}` });
      } else {
        // View/fill-in: selection = navigation, always drill down
        navigate({ to: `${basePath}/${fieldId}/details` });
      }
    },
    [navigate, basePath, stackPath, isEditing],
  );

  // Drill-down: "I want to see the details of this node"
  const handleDrillDown = useCallback(
    (fieldId: string) => {
      navigate({ to: `${basePath}/${fieldId}/details` });
    },
    [navigate, basePath],
  );

  const handleActivityClick = useCallback(() => {
    navigate({ to: `${basePath}/activity` });
  }, [navigate, basePath]);

  const handleInheritanceClick = useCallback(() => {
    navigate({ to: `${basePath}/inheritance` });
  }, [navigate, basePath]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  // Subscribe to formState during render so react-hook-form tracks these
  const { isDirty: hasDirtyForm } = form.formState;
  const hasAnyChanges = hasChanges || hasDirtyForm;

  const panelTitle = list ? list.name : `List ${listId.slice(0, 8)}`;

  const statusBadge =
    showStatusBadge && builtList ? (
      <Badge
        variant={builtList.status === 'open' ? 'default' : 'secondary'}
        className="text-xs"
      >
        {builtList.status === 'open' ? 'Open' : 'Completed'}
      </Badge>
    ) : null;

  const headerActions = (
    <>
      {statusBadge}
      {!isEditing ? (
        <Button variant="ghost" size="sm" onClick={enterEditMode}>
          <Pencil className="h-4 w-4" />
          Edit
        </Button>
      ) : (
        <>
          <Button variant="ghost" size="sm" onClick={handleRevert}>
            <X className="h-4 w-4" />
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!hasAnyChanges}>
            <Check className="h-4 w-4" />
            Save
          </Button>
        </>
      )}
    </>
  );

  const progress =
    showProgress && builtList ? calculateProgress(builtList.tree) : undefined;

  const panelBody = (
    <>
      {/* Metadata form */}
      <EntityForm
        {...entityForm}
        id="list-details-form"
        hideActions={true}
        onSubmit={form.handleSubmit(
          async () => await handleSave(),
          () => {
            /* validation errors shown inline */
          },
        )}
      />

      {/* Summary footer (progress + activity + inheritance) — non-edit only */}
      {!isEditing && (
        <ListSummaryFooter
          list={list}
          progress={progress}
          onActivityClick={handleActivityClick}
          onInheritanceClick={handleInheritanceClick}
        />
      )}

      {/* Field tree */}
      <ListTreeEditor
        tree={builtList.tree}
        activeFieldId={nodeId}
        onSelectField={handleSelectField}
        onDrillDown={handleDrillDown}
        locale={previewLocale}
        onEnterEditMode={enterEditMode}
      />
    </>
  );

  return (
    <Panel
      {...panelProps}
      title={panelTitle}
      actions={actions}
      headerActions={headerActions}
    >
      {wrapBody ? wrapBody(panelBody, list) : panelBody}
    </Panel>
  );
}
